/**
 * Onboarding controller (view) — drives the scripted "Mexican Pizza" first
 * session: coach marks + step transitions. Game logic lives in onboardingState;
 * this only reads state, dispatches the UI-driven transitions, and paints the
 * coach bubble + a pulsing highlight ring over the relevant element.
 */
import { t } from '../i18n/index.js';
import { getState, getViewState, dispatch } from './renderer.js';
import { ONBOARDING_COIN_ID } from '../game/onboardingState.js';

interface StepView { key: string; target: () => Element | null; }

const STEPS: Record<number, StepView> = {
  0: { key: 'ob_step0', target: () => document.getElementById('coin-touch-overlay') },
  1: { key: 'ob_step1', target: () => document.querySelector('.tab-item[data-tab="exchange"]') },
  2: { key: 'ob_step2', target: () => document.querySelector(`[data-coin="${ONBOARDING_COIN_ID}"]`) },
  3: { key: 'ob_step3', target: () => document.querySelector('[data-mode="sell"], .cd-sell, #cd-sell') },
};

let overlay: HTMLElement | null = null;
let bubble: HTMLElement | null = null;
let ring: HTMLElement | null = null;
let timer: number | null = null;
let celebrateUntil = 0;

function build(): void {
  overlay = document.createElement('div');
  overlay.id = 'ob-overlay';
  ring = document.createElement('div');
  ring.id = 'ob-ring';
  bubble = document.createElement('div');
  bubble.id = 'ob-bubble';
  const skip = document.createElement('button');
  skip.id = 'ob-skip';
  skip.textContent = t('ob_skip');
  skip.addEventListener('click', () => dispatch({ type: 'ONBOARDING_SKIP' } as never));
  overlay.append(ring, bubble, skip);
  document.body.appendChild(overlay);
}

function teardown(): void {
  if (timer !== null) { clearInterval(timer); timer = null; }
  overlay?.remove();
  overlay = bubble = ring = null;
}

/** Position the pulsing ring over a target element (hidden when absent). */
function placeRing(el: Element | null): void {
  if (!ring) return;
  if (!el) { ring.style.display = 'none'; return; }
  const r = el.getBoundingClientRect();
  if (r.width === 0) { ring.style.display = 'none'; return; }
  Object.assign(ring.style, {
    display: 'block', left: `${r.left - 6}px`, top: `${r.top - 6}px`,
    width: `${r.width + 12}px`, height: `${r.height + 12}px`,
  });
}

function tick(): void {
  const ob = getState().onboarding;
  // Advance step 0 -> 1 by tap count; step 1 -> 2 on entering the exchange.
  if (ob.step === 0) dispatch({ type: 'ONBOARDING_TAP_CHECK' } as never);
  if (ob.step === 1 && getViewState().currentTab === 'exchange') {
    dispatch({ type: 'ONBOARDING_ENTER_EXCHANGE' } as never);
  }
  if (ob.done) {
    // sell just completed the twist -> a short celebration, then finish.
    if (!celebrateUntil) celebrateUntil = Date.now() + 3400;
    if (bubble) bubble.textContent = t('ob_step4');
    placeRing(null);
    if (Date.now() >= celebrateUntil) teardown();
    return;
  }
  const view = STEPS[ob.step];
  if (!view || !bubble) return;
  bubble.textContent = t(view.key);
  placeRing(view.target());
}

/** Start the flow for a new player; no-op (and no overlay) once done. */
export function initOnboarding(): void {
  if (getState().onboarding.done) return;
  build();
  timer = window.setInterval(tick, 160);
  tick();
}
